import { type HiringAsAnIndividual_userProfile$key } from '#app/__generated__/relay/HiringAsAnIndividual_userProfile.graphql.js';
import { InlineEditContainer } from '#app/components/InlineEditContainer/InlineEditContainer.js';
import { Text } from '#app/components/Primitives/Text.js';
import { EditIcon } from '@contra/icons/icons';
import { graphql, useFragment } from '@contra/relay';
import { lazy, Suspense, useState } from 'react';
import { css, styled } from 'styled-components';

const EditHiringDetailsModal = lazy(async () => {
  return {
    default: (
      await import('./EditHiringDetailsModal/EditHiringDetailsModal.js')
    ).EditHiringDetailsModal,
  };
});

const Container = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 16px;
`;

const CompanyDetails = styled.div`
  > * + * {
    margin-top: 4px;
  }

  strong {
    font-weight: 600;
  }
`;

const SubText = styled(Text).attrs({ textStyle: 'bodySmall' })`
  ${({ theme }) => css`
    color: ${theme.colorsV2.gray70};
  `}
`;

export const HiringAsAnIndividual = ({
  disableEditing = false,
  userProfileRef,
}: {
  readonly disableEditing?: boolean;
  readonly userProfileRef: HiringAsAnIndividual_userProfile$key;
}) => {
  const [editModalOpen, setEditModalOpen] = useState(false);

  const profile = useFragment<HiringAsAnIndividual_userProfile$key>(
    graphql`
      fragment HiringAsAnIndividual_userProfile on UserProfile {
        visitorCanEdit
        professionalTitle
        firstName
        organization {
          __typename
        }
      }
    `,
    userProfileRef,
  );

  const { visitorCanEdit } = profile;

  if (visitorCanEdit && !disableEditing) {
    return (
      <>
        <div style={{ textAlign: 'left', width: '100%' }}>
          <InlineEditContainer
            label="Edit your company"
            onClick={() => {
              if (visitorCanEdit) {
                setEditModalOpen(true);
              }
            }}
            visitorCanEdit={visitorCanEdit}
          >
            <Container>
              <CompanyDetails>
                <Text>
                  <strong>
                    {profile.professionalTitle ?? 'Hiring as an Individual'}
                  </strong>
                </Text>
                <SubText>
                  {profile.firstName} is not hiring through a company
                </SubText>
              </CompanyDetails>
            </Container>
            <EditIcon size={16} />
          </InlineEditContainer>
        </div>
        <Suspense>
          {editModalOpen ? (
            <EditHiringDetailsModal
              initialData={{
                company: null,
                isHiringAsIndividual: profile.organization === null,
                professionalTitle:
                  profile.professionalTitle ?? 'Hiring as an Individual',
              }}
              isOpen={editModalOpen}
              onClose={() => {
                setEditModalOpen(false);
              }}
            />
          ) : null}
        </Suspense>
      </>
    );
  }

  return (
    <Container>
      <CompanyDetails>
        <Text>
          <strong>
            {profile.professionalTitle ?? 'Hiring as an Individual'}
          </strong>
        </Text>
        <Text textStyle="bodySmall">
          {profile.firstName} is not hiring through a company
        </Text>
      </CompanyDetails>
    </Container>
  );
};
